import { isSame } from '@myre/common-dates';
import {
  DotsLoaderComponent,
  TagComponent,
  type MyuiStyleClassType,
  type MyuiStyleListType,
} from '@myre/ui-primitive-components';
import {
  browserDetection,
  DEFAULT_TRANSLATOR,
  slide,
  stretch,
  TranslatePipe,
  TRANSLATOR,
} from '@myre/ui-primitive-core';
import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';
import { OverlayService } from '@myre/ui-primitive-overlay';

import { type AnimationEvent } from '@angular/animations';
import { OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  input,
  model,
  NgZone,
  output,
  Output,
  Renderer2,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  type AfterViewInit,
  type OnChanges,
  type Provider,
  type SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { remove as removeDiacritics } from 'diacritics';
import { castArray, isNil } from 'lodash-es';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  takeUntil,
  tap,
} from 'rxjs';

import { FORM_ITEM_HEIGHT } from '../../form.constants';
import { type ISelectItem } from '../../questions/question-multi-base';

import { CheckboxComponent } from '../checkbox/checkbox.component';
import { TextboxComponent } from '../textbox/textbox.component';

import { dropdownPositions } from './constants';
import { DropdownTemplate } from './dropdown.directive';
import { searchTypeAttribute } from './helpers/dropdown.helpers';
import { SubThemeToTagTypePipe } from './pipes/sub-theme-to-tag-type.pipe';
import { DropdownSearchType } from './types/search.type';
import { type SubTheme, type Theme } from './types/theme-type';

export const DROPDOWN_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true,
};

export interface DropdownContext<V> {
  isOpen: boolean;
  $implicit: V | V[] | null;
}

@Component({
  selector: 'myre-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  animations: [slide, stretch],
  providers: [DROPDOWN_VALUE_ACCESSOR],
  imports: [
    CheckboxComponent,
    DotsLoaderComponent,
    EllipsisDirective,
    FormsModule,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    SubThemeToTagTypePipe,
    TagComponent,
    TextboxComponent,
    TranslatePipe,
  ],
})
export class DropdownComponent<T>
  implements AfterViewInit, ControlValueAccessor, OnChanges
{
  private readonly _ngZone = inject(NgZone);
  private readonly _document = inject(DOCUMENT);
  private readonly _renderer = inject(Renderer2);
  private readonly _elementRef = inject(ElementRef);
  private readonly _overlayService = inject(OverlayService);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _changeDetectionRef = inject(ChangeDetectorRef);
  private _translator =
    inject(TRANSLATOR, { optional: true }) ?? DEFAULT_TRANSLATOR;

  private readonly _destroyRef = inject(DestroyRef);

  protected get _singleValue(): (T & { value?: any }) | null {
    if (this.isMultiselect()) {
      return null;
    }

    return this.value as T & { value?: any };
  }

  protected get _multipleValues(): T[] {
    if (!this.isMultiselect()) {
      return [];
    }

    return this.value as T[];
  }

  public readonly id = input<string>('');

  public readonly picto = input<string>('');

  public readonly type = input<'select' | 'multiselect' | 'autocomplete'>(
    'select',
  );

  public readonly autocompleteKeep = input<boolean>(false);

  public readonly autocompleteKeepValue = model<ISelectItem<T> | null>(null);

  public readonly error = input(false);

  public readonly warning = input(false);

  public readonly key = input('label');

  public readonly trackBy = input<(v: T) => any | undefined>(undefined);

  public readonly placeholder = input<string>('');

  public readonly placeholderIcon = input<string>('');

  public readonly longPlaceholder = input<string>('');

  public readonly searchPlaceholder = input<string>('');

  public readonly emptyListPlaceholder = input<string>('_library.table.noData');

  public readonly emptyFilteredListPlaceholder = input<string>(
    '_library.table.noDataFiltered',
  );

  /**
   * @deprecated  From a UX perspective, disabling the scroll of the list should not occur.
   * In case of necessity, coordinate with the Design and FrontEnd team.
   */
  public readonly noScroll = input(false);

  public readonly adaptWidth = input(true);

  public readonly readonly = input(false);

  public readonly required = input(false);

  public readonly stretch = input(false);

  public readonly theme = input<Theme>('default');

  public readonly subTheme = model<SubTheme>('default');

  public readonly fixedHeight = input(false);

  public readonly wrapPanel = input(true);

  /** Shows the optional aside column (second slot). Only meaningful when an aside template is projected. */
  public readonly asideOpen = input(false);

  public readonly closeOnSelect = input(true);

  public readonly elementStyle = input<MyuiStyleListType | undefined>();
  public readonly elementStyleClass = input<MyuiStyleClassType | undefined>();

  public readonly labelStyle = input<MyuiStyleListType | undefined>();
  public readonly labelStyleClass = input<MyuiStyleClassType | undefined>();

  public readonly listStyle = input<MyuiStyleListType | undefined>();
  public readonly listStyleClass = input<MyuiStyleClassType | undefined>();

  @HostBinding('class.host-icon-only')
  public get isIconOnly(): boolean {
    return (
      this.isIconTheme() || (!!this.placeholderIcon() && !this.placeholder())
    );
  }

  /**
   * @deprecated Use `subTheme` instead
   */
  @Input()
  public set tertiary(tertiary: boolean) {
    if (tertiary) {
      this.subTheme.set('tertiary');
    }
  }

  /**
   * @deprecated Use `subTheme` instead
   */
  @Input()
  public set secondary(secondary: boolean) {
    if (secondary) {
      this.subTheme.set('secondary');
    }
  }

  public readonly shortLength = input<string>('');

  public readonly longLength = input<string>('');

  public readonly clearOnBlur = input(false);

  public readonly selectAllLabel = input('');

  public readonly disableSearch = input(false);

  public readonly searchProp = input('');

  public readonly searchType = input(DropdownSearchType.Soft, {
    transform: searchTypeAttribute,
  });

  public readonly list = model<ISelectItem<T>[]>([]);

  /** Renders the loader in place of the list, for a list fetched on opening. */
  public readonly loading = input(false);

  public readonly onFocus = output<void>();

  public readonly onBlur = output<void>();

  public readonly onStretchDone = output<any>();

  public readonly initDone = output<void>();

  @Output('onSelect')
  private _onSelect = new EventEmitter<T | T[]>();

  @Output('onInput')
  private _onInput = new EventEmitter<string>();

  private _isDestroyed = signal(false);

  public readonly templates = contentChildren(DropdownTemplate);

  public readonly rootElement = viewChild<ElementRef>('rootElement');

  public readonly input = viewChild<ElementRef>('inputElement');

  public readonly overlayContainer = viewChild<ElementRef>('overlayContainer');

  public readonly overlayElement =
    viewChild<TemplateRef<any>>('overlayElement');

  public readonly scrollableContainer = viewChild<ElementRef>(
    'scrollableContainer',
  );

  public disabled = false;

  private _keystroke: string = '';
  private _inMemoryValues: T | T[] = [];
  private _timeout: any;
  private _overlayRef: OverlayRef;

  private _focused = signal(false);

  private _onModalChange = new BehaviorSubject<boolean>(false);

  protected readonly _hoveredIndex = signal(-1);

  protected readonly _searchKeyword = signal('');

  protected readonly _displayOverlay = signal(false);

  protected readonly _safeList = computed(() => {
    return this.list() ?? [];
  });

  // Type
  public readonly isSelect = computed(() => {
    return this.type() === 'select';
  });

  public readonly isMultiselect = computed(() => {
    return this.type() === 'multiselect';
  });

  public readonly isSelectable = computed(() => {
    return this.isSelect() || this.isMultiselect();
  });

  /** The trigger is the bare `placeholderIcon`, whatever is selected; the panel is unchanged. */
  public readonly isIconTheme = computed(() => {
    return this.theme() === 'icon';
  });

  public readonly hasValue = computed(() => {
    return this._value().length > 0;
  });

  public readonly isAutocomplete = computed(() => {
    return this.type() === 'autocomplete';
  });

  // Search
  public readonly isSearching = computed(() => {
    return this._searchKeyword().length > 0;
  });

  public readonly isInputSearch = computed(() => {
    return (
      !this.disableSearch() && this.searchType() === DropdownSearchType.Input
    );
  });

  public readonly filteredList = computed(() => {
    let list = this._safeList();

    const words = this._searchWords();

    if (words.length) {
      list = list.filter((_, index) => {
        const content = this._itemsContents().at(index) ?? '';

        return words.every(word => content.includes(word));
      });
    }

    return list;
  });

  private readonly _itemsContents = computed(() => {
    if (this.isInputSearch()) {
      return this._safeList().map(item => {
        let result = JSON.stringify(item);
        const key = this.searchProp() || this.key();

        if (key) {
          // @ts-expect-error
          result = String(item[key] ?? '');
        }

        return removeDiacritics(result.toLowerCase().trim());
      });
    }

    return [];
  });

  private readonly _searchWords = computed(() => {
    const keyword = removeDiacritics(
      this._searchKeyword().toLowerCase().trim(),
    );

    if (keyword) {
      return Array.from(new Set(keyword.split(' ').filter(Boolean)));
    }

    return [];
  });

  // Focus state
  public readonly isFocused = computed(() => this._focused());

  // Templates
  public readonly itemTemplate = computed(() => {
    return this._getTemplate(this.templates(), 'item');
  });

  public readonly selectedItemTemplate = computed(() => {
    return this._getTemplate<DropdownContext<T>>(
      this.templates(),
      'selectedItem',
    );
  });

  public readonly emptyTemplate = computed(() => {
    return this._getTemplate(this.templates(), 'empty');
  });

  public readonly headerTemplate = computed(() => {
    return this._getTemplate(this.templates(), 'header');
  });

  public readonly footerTemplate = computed(() => {
    return this._getTemplate(this.templates(), 'footer');
  });

  public readonly asideTemplate = computed(() => {
    return this._getTemplate(this.templates(), 'aside');
  });

  public readonly templateContext = computed<DropdownContext<T>>(() => {
    return {
      isOpen: this.isFocused(),
      $implicit: this._value(),
    };
  });

  public get focused(): boolean {
    return this._focused();
  }

  public updateIsFocused(focused: boolean): void {
    if (focused && !this._isDestroyed()) {
      this.onFocus.emit();
    }

    this._focused.set(focused);
    this._updateDisplayOverlay();
  }

  private _value = signal<T[]>([]);

  public get value(): T | T[] {
    if (this.isMultiselect()) {
      return this._value();
    }

    return this._value().length ? this._value()[0] : null;
  }

  public set value(value: T | T[]) {
    this._inMemoryValues = value;

    if (isNil(value)) {
      this._value.set([]);
      this._hoveredIndex.set(-1);

      return;
    }

    const values = castArray(value);

    this._value.set(
      (this.isMultiselect() ? values : [values[0]]).filter(v =>
        this._safeList().some(i => this.equals(v, i.value)),
      ),
    );
  }

  public get isAllSelected(): boolean {
    return this._safeList()
      .filter(el => !el.disabled)
      .every(el => this._value().includes(el.value));
  }

  public get isDesktop(): boolean {
    return browserDetection.isDesktop;
  }

  constructor() {
    effect(() => {
      const isFirstElementFocused =
        this._safeList().length &&
        this._hoveredIndex() === -1 &&
        this.isAutocomplete();

      if (isFirstElementFocused) {
        this._hoveredIndex.set(0);
      }

      this._updateDisplayOverlay();
    });

    effect(() => {
      this._autofillSingleRequiredValue();
    });

    this._onModalChange
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(visible => !visible),
        tap(() => {
          this._searchKeyword.set('');
        }),
      )
      .subscribe();
    this._destroyRef.onDestroy(() => this._isDestroyed.set(true));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.list) {
      if (Array.isArray(changes.list.currentValue)) {
        this._hoveredIndex.set(
          Math.min(changes.list.currentValue.length - 1, this._hoveredIndex()),
        );
      } else {
        this._hoveredIndex.set(-1);
      }

      this.value = this._inMemoryValues;
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this._isDestroyed()) {
        return;
      }

      this.initDone.emit();
    });
  }

  public selectedLabel(forValue: T): string {
    const value = this._safeList().find(item =>
      this.equals(item.value, forValue),
    );

    return value?.label || '';
  }

  @HostListener('keydown.arrowup', ['$event'])
  protected _onKeyArrowUp(event: Event): void {
    if (!this._focused() || !this._safeList().length) {
      return;
    }

    event.preventDefault();

    const steps = this._getStepsToFirstEnabled(this._hoveredIndex() - 1);

    this._hoveredIndex.set(
      Math.max(
        0,
        this._hoveredIndex() === -1
          ? this._safeList().length - 1
          : this._hoveredIndex() - steps,
      ),
    );
    this._scrollToHovered();
  }

  @HostListener('keydown.arrowdown', ['$event'])
  protected _onKeyArrowDown(event: Event): void {
    if (!this._focused() || !this._safeList().length) {
      return;
    }

    event.preventDefault();

    const steps = this._getStepsToFirstEnabled(this._hoveredIndex() + 1);

    this._hoveredIndex.set(
      Math.min(this._safeList().length - 1, this._hoveredIndex() + steps),
    );
    this._scrollToHovered();
  }

  @HostListener('keydown', ['$event'])
  protected _onKeyDown(event: KeyboardEvent): void {
    if (
      !this._focused() ||
      !this._safeList().length ||
      this.isAutocomplete() ||
      this.disableSearch()
    ) {
      return;
    }

    event.preventDefault();

    if (event.key && event.key.length === 1) {
      const str = removeDiacritics(event.key.toLowerCase());

      if (/[a-z0-9 ]/.test(str)) {
        this._keystroke += str;
        this._keystroke = this._keystroke.trim();

        const foundIndex = this._safeList().findIndex(
          item =>
            !item.disabled &&
            // @ts-expect-error
            removeDiacritics(this._translator(item[this.key()]))
              .toLowerCase()
              .startsWith(this._keystroke),
        );

        if (foundIndex > -1) {
          this._hoveredIndex.set(foundIndex);
          this._scrollToHovered();
        }
      }
    }

    clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      this._keystroke = '';
    }, 500);
  }

  @HostListener('keyup.enter')
  protected _onKeyEnter(): void {
    if (!this._focused() || !this._safeList().length) {
      return;
    }

    this.select(this._hoveredIndex());

    if (!this.isMultiselect() && this.closeOnSelect()) {
      this.blur();
    }

    this._changeDetectionRef.detectChanges();
  }

  @HostListener('keyup.esc')
  protected _onKeyEscape(): void {
    if (!this._focused() || !this._safeList().length) {
      return;
    }

    this.blur();
    this._changeDetectionRef.detectChanges();
  }

  protected _selectFromList(
    event: Event,
    index: number,
    blur: boolean = false,
  ): void {
    event?.stopPropagation();

    const item = this.filteredList()[index];
    const itemIndex = this._safeList().indexOf(item);

    this.select(itemIndex, blur);
  }

  @HostListener('keydown.Tab')
  protected _tab(): void {
    if (!this._focused() || !this._safeList().length) {
      return;
    }

    setTimeout(() => {
      if (
        this.focused &&
        !this._elementRef.nativeElement.contains(this._document.activeElement)
      ) {
        this._ngZone.run(() => {
          this.blur();
          this._changeDetectionRef.detectChanges();
        });
      }
    });
  }

  /**
   * Before the animation starts, we calculate the height of the list and apply it to its container.
   * The animation is then played on the child of the container,
   * which allows us to preset the final value of the list's height so that the cdk overlay can calculate its position based on the correct height.
   */
  protected _animationStart(event: AnimationEvent): void {
    if (!this.focused) {
      return;
    }
    const ul = event.element.firstElementChild;
    const height = ul.clientHeight;

    if (!height) {
      return;
    }

    const overlayContainer = this.overlayContainer();

    if (overlayContainer?.nativeElement) {
      overlayContainer.nativeElement.style.height = `${
        height + FORM_ITEM_HEIGHT * this._safeList().length
      }px`;
    }

    this._overlayRef.updatePosition();
  }

  /**
   * After the animation is done, we remove the overlay
   */
  protected _animationDone(): void {
    if (this.focused) {
      return;
    }

    this._overlayRef.detach();
    this._onModalChange.next(false);
  }

  protected _accessor(item: ISelectItem<T>, key: string): string {
    return item[key as keyof ISelectItem<T>] ?? '';
  }

  public select(index: number, blur: boolean = false): void {
    if (index === -1) {
      return;
    }

    if (index !== null && this._safeList()[index]?.disabled) {
      return;
    }

    const type = this.type();

    if (type === 'autocomplete') {
      const value = index === null ? null : this._safeList()[index]?.value;

      this._onSelect.emit(value);
      this.writeValue(value);
      this._onChange(value);

      if (this.input()) {
        this._renderer.setProperty(this.input().nativeElement, 'value', '');
      }

      this.autocompleteKeepValue.set(null);

      if (index !== null && this.autocompleteKeep()) {
        this.autocompleteKeepValue.set(this._safeList()[index]);
      }

      this.list.set([]);
    } else if (index !== null) {
      const selected = this._safeList()[index].value;
      let value: T[] | T;

      if (type === 'multiselect') {
        const alreadySelectedIndex = this._value().findIndex(v =>
          this.equals(v, selected),
        );

        if (alreadySelectedIndex === -1) {
          value = [...this._value(), selected];
        } else {
          value = [...this._value()];
          value.splice(alreadySelectedIndex, 1);
        }
      } else {
        value = selected;
      }

      this._onSelect.emit(value);
      this.writeValue(value);
      this._onChange(value);
    }

    if (blur && this.closeOnSelect()) {
      this._hoveredIndex.set(-1);
      this.blur();
    } else {
      this._hoveredIndex.set(index);
      this.rootElement().nativeElement.focus();
    }
  }

  /**
   * When a required single-select offers exactly one selectable option, pick it
   * automatically so the user doesn't have to. Stays inert otherwise: only on a
   * required `select` with no current value and a single enabled, non-null option.
   */
  private _autofillSingleRequiredValue(): void {
    if (!this.required() || !this.isSelect() || this._value().length > 0) {
      return;
    }

    const selectableItems = this._safeList().filter(item => !item.disabled);

    if (selectableItems.length !== 1) {
      return;
    }

    const value = selectableItems[0].value;

    if (isNil(value)) {
      return;
    }

    this._onSelect.emit(value);
    this.writeValue(value);
    this._onChange(value);
  }

  public tagRemoveClicked(event: Event, item: T): void {
    event.stopPropagation();
    this.unselect(item);
    // Removing a tag never blurs the control, so mark it touched here;
    // otherwise clearing the last tag won't surface a required error.
    this._onTouched();
  }

  public unselect(item: T): void {
    this.select(this._safeList().findIndex(v => this.equals(v.value, item)));
  }

  public focus(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this._focused()) {
      return;
    }

    if (this._isSelectedTag(event)) {
      return this.blur(event);
    }

    if (this.disabled || this.readonly() || this._focused()) {
      return;
    }

    this.updateIsFocused(true);

    if (!this.isMultiselect()) {
      const value = Array.isArray(this.value) ? this.value[0] : this.value;
      this._hoveredIndex.set(
        this._safeList().findIndex(v => this.equals(v.value, value)),
      );
    }

    // Open the overlay as soon as it's focused unless the dropdown is in stretch mode
    // in that case, we wait until the end of the animation to open the overlay
    // see _stretchDone method
    if (!this.stretch()) {
      this._openOverlay();
    }
  }

  private _getStepsToFirstEnabled(startIndex: number): number {
    let steps = 1;
    let i = startIndex;

    while (this._safeList()[i] && this._safeList()[i].disabled) {
      steps++;
      i++;

      if (i === this._safeList().length) {
        return;
      }
    }

    return steps;
  }

  private _openOverlay(): void {
    const { overlayRef, positionStrategy } = this._overlayService.open({
      type: 'template',
      viewContainerRef: this._viewContainerRef,
      content: this.overlayElement(),
      connectTo: this.rootElement(),
      backdrop: {
        isTransparent: true,
        onClick: _ => {
          this.blur();
        },
      },
      withPositions: dropdownPositions,
      scrollStrategy: 'reposition',
      styles: this.adaptWidth()
        ? {
            minWidth: this.rootElement().nativeElement.offsetWidth,
          }
        : {
            width: this.rootElement().nativeElement.offsetWidth,
          },
    });

    this._overlayRef = overlayRef;
    this._onModalChange.next(true);

    positionStrategy.positionChanges
      .pipe(
        takeUntil(
          this._onModalChange.pipe(
            distinctUntilChanged(),
            filter(visible => !visible),
          ),
        ),
      )
      .subscribe(value => {
        const overlayContainer = this.overlayContainer();

        if (!overlayContainer?.nativeElement) {
          return;
        }

        const positionTop = value.connectionPair.originY === 'top';
        const overlayContainerElement = overlayContainer.nativeElement;
        const classNames = [
          'top',
          this.adaptWidth() ? 'adapt-width' : null,
        ].filter(Boolean);

        // add or remove class for animation
        for (const className of classNames) {
          if (positionTop) {
            this._renderer.addClass(overlayContainerElement, className);
          } else {
            this._renderer.removeClass(overlayContainerElement, className);
          }
        }
      });
  }

  public clickInside(): void {
    this.input()?.nativeElement.focus();
  }

  public blur(event?: Event): void {
    let target: HTMLElement;

    if (event instanceof MouseEvent || event instanceof FocusEvent) {
      target = event.relatedTarget as HTMLElement;
    }

    if (
      !this._focused() ||
      target === this.rootElement().nativeElement ||
      this._isListTag(event) ||
      this._isSelectedTag(event)
    ) {
      return;
    }

    if (!this._isDestroyed()) {
      this.onBlur.emit();
    }

    if (this.clearOnBlur() && this.input()) {
      this.input().nativeElement.value = '';
    }

    if (event) {
      event.stopPropagation();
    }

    this.updateIsFocused(false);
    this._hoveredIndex.set(-1);
    this._changeDetectionRef.detectChanges();
    this._onTouched();
  }

  protected _emitOnInput(event: Event): void {
    this._onInput.emit((event.target as HTMLInputElement).value);
  }

  public writeValue(value: T[] | T): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public isSelected(item: T): boolean {
    return this._value().some(v => this.equals(v, item));
  }

  public equals(val1: T, val2: T): boolean {
    if (val1 instanceof Date && val2 instanceof Date) {
      return isSame(val2, val1);
    }

    const trackBy = this.trackBy();

    if (trackBy) {
      return val1 && val2 && trackBy(val1) && trackBy(val1) === trackBy(val2);
    }

    return val1 === val2;
  }

  protected _getPlaceholder(): string {
    const longPlaceholder = this.longPlaceholder();

    if (longPlaceholder) {
      return this._focused() ? longPlaceholder : this.placeholder();
    }

    return this.placeholder() || '';
  }

  protected _stretchDone(e: any): void {
    // Only emit if the component is still alive (not destroyed)
    if (this._isDestroyed()) {
      return;
    }

    this.onStretchDone.emit(e);

    // If the dropdown is in stretch mode, we open the overlay after the animation only if the input is already and still focused
    if (this.focused) {
      this._openOverlay();
    }
  }

  public toggleSelectAll(): void {
    // Fetch the disabled items that are already checked (e.g. when a disabled item is set as an initialValue)
    const disabledValues = this._value().filter(
      value => this._safeList().find(el => el.value === value)?.disabled,
    );

    if (this.isAllSelected) {
      // Empty the value array, except for disabled items
      this.writeValue(disabledValues);
      this._onChange(disabledValues);
    } else {
      // Fill the value array, without changing disabled items
      const list = this._safeList()
        .filter(el => !el.disabled)
        .map(el => el.value);

      this.writeValue([...list, ...disabledValues]);
      this._onChange([...list, ...disabledValues]);
    }
  }

  private _scrollToHovered(): void {
    const boundingRects = this._getBoundingRects();

    if (!boundingRects) {
      return;
    }

    const hiddenTopDelta =
      boundingRects.liBoundingRect.top -
      boundingRects.scrollableBoundingRect.top;

    if (hiddenTopDelta < 0) {
      this.scrollableContainer().nativeElement.scrollTop += hiddenTopDelta;

      return;
    }

    const hiddenBottomDelta =
      boundingRects.liBoundingRect.top +
      boundingRects.liBoundingRect.height -
      boundingRects.scrollableBoundingRect.top -
      boundingRects.scrollableBoundingRect.height;

    if (hiddenBottomDelta > 0) {
      this.scrollableContainer().nativeElement.scrollTop += hiddenBottomDelta;
    }
  }

  private _getBoundingRects(): {
    liBoundingRect: any;
    scrollableBoundingRect: any;
  } | null {
    const scrollableContainerElement = this.scrollableContainer().nativeElement;

    const li = scrollableContainerElement.querySelector(
      `[index="${this._hoveredIndex()}"]`,
    );

    if (!li) {
      return null;
    }

    return {
      liBoundingRect: li.getBoundingClientRect(),
      scrollableBoundingRect:
        scrollableContainerElement.getBoundingClientRect(),
    };
  }

  private _isListTag(event: any): boolean {
    const overlayContainer = this.overlayContainer();

    return (
      event &&
      event.relatedTarget &&
      overlayContainer &&
      overlayContainer.nativeElement.contains(event.relatedTarget)
    );
  }

  private _isSelectedTag(event: any): boolean {
    return (
      event &&
      event.relatedTarget &&
      event.relatedTarget.classList.contains('delete') &&
      this._elementRef.nativeElement.contains(event.relatedTarget)
    );
  }

  private _getTemplate<C = unknown>(
    templates: readonly DropdownTemplate[],
    key: string,
  ): TemplateRef<C> | undefined {
    return templates.find(t => t.type() === key)?.templateRef;
  }

  private _onChange = (_: any) => {};

  private _onTouched = () => {};

  private _updateDisplayOverlay(): void {
    this._displayOverlay.set(this.focused);
    this._onModalChange.next(this._displayOverlay());
  }
}
